import { ReactNode, createContext } from 'react';

import { receivedDigitalApi } from '@api/index';
import { useGetSettingsQuery } from '@api/settings';
import type { Setting } from '@apiContract';
import { LoadingScreen } from '@components/LoadingScreen';
import { PREFERRED_TIME_ZONE } from 'src/constants';
import { getTimeZone } from 'src/hooks/useDisplayTimeZone';
import { getSettingValue } from 'src/hooks/useSettings';
import { useGetMailroomQuery } from 'src/redux/api/mailroom';
import { useDispatch } from 'src/redux/store';
import { clientTimeZone, setClientTimeZone } from 'src/utils/timeZones';

export const SettingsContext = createContext<{ [key: string]: Setting }>({});

const TIME_ZONE_DEPENDENT_TAGS = ['Pieces', 'PiecesCounters', 'RS', 'RSPiece'] as const;

type ConfigurationProviderProps = {
  children: ReactNode;
};

export const ConfigurationProvider = (props: ConfigurationProviderProps) => {
  const { children } = props;

  const dispatch = useDispatch();
  const { data: settings, isLoading } = useGetSettingsQuery();
  const { data: mailroom, isLoading: isMailroomLoading } = useGetMailroomQuery();

  // A missing mailroom (account setup) is fine: getTimeZone falls back to the browser zone.
  if (!settings || isLoading || isMailroomLoading) return <LoadingScreen />;

  const timeZone = getTimeZone(getSettingValue(settings[PREFERRED_TIME_ZONE]) as string, mailroom);

  // Compared with the zone currently sent in the header, so switching back to the local zone applies too.
  if (clientTimeZone !== timeZone) {
    setClientTimeZone(timeZone);
    dispatch(receivedDigitalApi.util.invalidateTags([...TIME_ZONE_DEPENDENT_TAGS]));
  }

  return <SettingsContext.Provider value={settings}>{children}</SettingsContext.Provider>;
};
